import Axios from 'axios';
import { Subject } from '@/models/subject';
import { Tutor } from '@/modules/tutor';
import { Student } from '@/modules/student';
import { SchedulePerson } from '@/models/schedulePerson';
import { GradeYears } from '@/enums/gradeYears';

interface SearchCondition {
  perPage: number;
  page: number;
  sort: string;
  startDate: Date | null;
  endDate: Date | null;
  tutorId: string;
  subjectId: number;
}

interface Reservation {
  unixtimeStartDate: number;
  unixtimeEndDate: number;
  tutor: Tutor | null;
  subject: Subject;
  student: Reserver | null;
  memo: string;
}

interface Reserver {
  studentId: string;
  firstName: string;
  lastName: string;
  gradeYear: GradeYears;
}

const emptyReservation = (): Reservation => {
  return {
    unixtimeStartDate: 0,
    unixtimeEndDate: 0,
    tutor: null,
    subject: {id: 0, name: '', color: ''},
    student: null,
    memo: '',
  };
};

interface ReportCreateArgs {
  studentId: string;
  studentName: string;
  startDate: Date | null;
  endDate: Date | null;
  subjects: Subject[];
}

// 型の Export
export {
  SearchCondition,
  Reservation,
  ReportCreateArgs,
};

interface State {
  isLoading: boolean;
  countAll: number;
  searchCondition: SearchCondition;
  list: Reservation[];
  reportCreateArgsList: ReportCreateArgs[];
}

const mapState = {
  isLoading: false,
  countAll: 0,
  searchCondition: {
    perPage: 20,
    page: 1,
    sort: '',
    startDate: null,
    endDate: null,
    tutorId: '',
    subjectId: 0,
  },
  list: [],
  reportCreateArgsList: []
};

const mapMutations = {
  setIsLoading(state: State, isLoading: boolean) {
    state.isLoading = isLoading;
  },

  setList(state: State, list: Reservation[]) {
    state.list = list;
    state.isLoading = false;
  },

  setReportCreateArgsList(state: State, argsList: ReportCreateArgs[]) {
    state.reportCreateArgsList = argsList;
  },

  setCountAll(state: State, countAll: number) {
    state.countAll = countAll;
  }
};

const mapActions = {
  async getListRequest({ commit, state }: any) {
    commit('setIsLoading', true);
    const response = await Axios.post('/api/Reservation/Search', state.searchCondition)
      .catch((error) => {
        commit('setList', []);
        throw error;
      });
    if (response) {
      commit('setList', response.data.list);
      commit('setCountAll', response.data.countAll);
    } else {
      commit('setList', []);
      commit('setCountAll', 0);
    }
  },

  async outputRequest({ commit, state }: any, schoolName: string) {
    const response = await Axios.post(
      '/api/Reservation/Output',
      state.searchCondition,
      { responseType: 'blob' },
    ).catch((error) => {
      throw error;
    });

    let fileName = '';
    const disposition = response.headers['content-disposition'];
    if (disposition && disposition.indexOf('attachment') !== -1) {
      const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
      if (matches != null && matches[1]) {
        fileName = decodeURI(matches[1].replace(/['"]/g, ''));
      }
    }

    fileName = schoolName + '_' + fileName;

    if (!(window.navigator as any).msSaveOrOpenBlob) {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      const url = (window.navigator as any).msSaveOrOpenBlob(new Blob([response.data]), fileName);
    }

    return true;
  }
};

// ストアの Export
export default {
  namespaced: true,
  state: mapState,
  actions: mapActions,
  mutations: mapMutations,
};
